import {XdSentry}   from "@Common/libs/sentry/xd-sentry";
import {cs}         from "@Common/react/chain-services";
import {FComponent} from "@Common/react/f-component";
import {fragments}  from "@Common/react/fragments";
import {Invoke}     from "@Common/react/invoke";
import {CcApis3}    from "@Common/tools/cc-integration/cc_apis/cc-apis-3";
import {Types}      from "@Common/types";
import {loadScript} from "@Common/ui-components/browser/load-script";
import {isDevXd}    from "@Common/ui-components/debug/is-dev";

import {downloadProofs} from "@Common/tools/cc-integration/common/download-proofs";

interface S {
    currentSurfaceIndex  : any,
    fullScreen           : boolean,
    initialized          : boolean,
    error                : boolean,
    modifiedItem         : string,
    modifiedSurface      : string,
    productModel         : any,
}
interface P {
    apis                     : Types.Apis.Loaders.XpressoLoadApis,
    ccApis                   : Types.Apis.Loaders.CustomersCanvasApis,
    ccHostResponse           : any,
    ccConfig                 : any,
    changedSinceLastSave     : any,
    systemConstants          : Types.Apis.Models.SystemConstants,
    reload                   : () => void,
    idmlPath                 : any,
    itemId                   : any,
    userData                 : any,
    onNotifyFailover         : (internetIsConnected:boolean, failoverServerIsAvailable:boolean, changesSaved:boolean) => void,
    next                     : any,
}

export class CcEditor extends FComponent<P,S>  {
    ccVersion;
    editorDom;
    editingStateId;
    editor;
    items;
    product;
    stateId;

    constructor(props, context) {
        super(props, context);

        if (isDevXd()) console.log("CcEditor Props", this.props);

        // refresh the page if provided host doesn't match the host on window["CustomersCanvas"] - this is a temporary
        // solution to an issue where the CC Editor stays pointed to a previous CC server when loading the editor in
        // APM, using the Cancel button, then selecting another product - there should be a way to flush CC resources
        // from the browser, but just doing a full page refresh for now
        if (window["CustomersCanvas"]?.IframeApi.editorUrl &&
            this.props.ccHostResponse.value?.ccHost &&
            this.props.ccHostResponse.value.ccHost !== window["CustomersCanvas"].IframeApi.editorUrl) {
            location.reload();
        }

        this.state = {
            currentSurfaceIndex: 0,
            fullScreen: false,
            initialized: false,
            error: false,
            modifiedItem: null,
            modifiedSurface: null,
            productModel: null,
        };
    };

    // Calls a method against the CC editor. Any CC iframe API calls should be made through this intermediary so that
    // failures can be intercepted for failover handling.
    callEditorMethod = async (name, ...args) => {
        // run the iframe API method - return if no exception is thrown
        try {
            return await this.editor[name](...args);
        }
        // FAILOVER ACTIONS
        catch (err) {
            if (err.exception?.ajaxResponse && err.exception.ajaxResponse.status !== 200) {
                const currentEditorHost = this.editor._context.editorUrl;
                const currentEditorVersion = await this.editor.eval("spEditor.productHandler.product.version");

                // check for next available host
                const ccApi = CcApis3({host: this.props.systemConstants.DS_SERVER_URL});
                const failoverHostData = await ccApi.getHost({
                    stateId: this.stateId
                }).catch(() => null); // return null if can't reach host service

                // if no host found, let the user know there was an error
                if (!failoverHostData?.ccHost) {
                    this.logFailoverIncident("", err);
                    this.props.onNotifyFailover(failoverHostData !== null, false, false);
                    return null;
                }

                // log the incident
                this.logFailoverIncident(failoverHostData.ccHost, err);

                // if same host was found, then momentary issue - retry the original call and continue as normal
                if (failoverHostData.ccHost === currentEditorHost) return this.callEditorMethod(name, ...args);

                /*
                // DISABLING SAVE AGAINST FAILOVER - THIS CAUSES AN ERROR IF IMAGES WERE ADDED SINCE THE LAST SAVE -
                // BEFORE RE-ENABLING, CHECK TO SEE IF IMAGE REFERENCES IN THE EDITOR CAN BE MODIFIED TO POINT TO THE
                // FAILOVER SERVER

                // Additional info:
                //   If adding an image a URL like this will load the image:
                //   https://ds-dev02.xpressdocs.com/api/ccviewer/img?f=b32e9921-4525-4695-8375-5cb4c5abb1e4&w=416&h=278
                //   The same URL but against ds-dev03 will not load. After saving, the image is still not available
                //   on ds-dev03. After refreshing the page and reloading the editor, then the ds-dev03 path works. This
                //   may mean that images are only stored in a cached version of the state, local to the DS server (not
                //   in S3), and saving changes against the failover may not be feasible.

                // if a new host was returned adjust the editor host to the failover host
                await this.editor.eval("spEditor.configuration.appDomainUrl = '" + failoverHostData.ccHost + "'")
                this.editor._context.editorUrl = failoverHostData.ccHost;
                if (this.props.ccHostResponse.value?.ccHost) this.props.ccHostResponse.value.ccHost = failoverHostData.ccHost; // when re-enabling, update to use ccHostResponse.onChange

                // save the state
                const saveResponse = await this.saveProductState(this.stateId);
                const saveWasSuccessful = saveResponse?.stateId === this.stateId;
                */
                const saveWasSuccessful = false; // TODO: Remove this after uncommenting the above

                this.props.onNotifyFailover(true, true, saveWasSuccessful);
            }
            return null;
        }
    };

    // Runs a CcApis3 function. Any calls should be made through this intermediary so that failures can be intercepted
    // for failover handling.
    callCCApi = async <T extends (...any)=>any>(fn:T, ...args:any[]):Promise<ReturnType<T>> => {
        // run the function and return if the value isn't null - null will be returned in cases of errors due to the
        // nullException wrapper being used in CcApis3 - this assumes no services ever need to return null as a
        // legitimate response
        const response = await fn(...args);
        if (response !== null
            || fn.name === "authenticateState" // if calling authenticate-state, null responses are expected and anything returned should be passed back
        ) return response;

        // FAILOVER ACTIONS

        // find the editor host and version
        const currentEditorHost = this.editor._context.editorUrl;
        const currentEditorVersion = await this.editor.eval("spEditor.productHandler.product.version");

        // check for next available host
        const ccApi = CcApis3({host: this.props.systemConstants.DS_SERVER_URL});
        const failoverHostData = await ccApi.getHost({
            stateId: this.stateId
        }).catch(() => null); // return null if can't reach host service

        // if no host found, let the user know there was an error
        if (!failoverHostData?.ccHost) {
            this.logFailoverIncident();
            this.props.onNotifyFailover(failoverHostData !== null, false, false);
            return null;
        }

        // log the incident
        this.logFailoverIncident(failoverHostData.ccHost);

        // if same host was found, then momentary issue - retry the original call and continue as normal
        if (failoverHostData.ccHost === currentEditorHost) return this.callCCApi(fn, ...args);

        this.props.onNotifyFailover(true, true, false);
    };

    // Logs the failover incident. If there is no failover host, then this will be marked as a critical issue.
    logFailoverIncident = (retryHost = "", errorData = {}) => {
        const originalHost = this.editor._context.editorUrl;

        let title = "Customer's Canvas could not reach the DS server";
        if (!retryHost) title += " - No failover host is available!";

        let message = "A failover incident has occurred when attempting communication with " + originalHost + ". ";
        if (!retryHost) message += "No failover host is available!";
        else if (retryHost === originalHost) message += "The request is being retried with the same host after validating connectivity. If this error persists, reboot " + originalHost + ".";
        else message += "After refresh, the editor will be loaded from " + retryHost + ".";

        XdSentry.addBreadcrumb({
            category: "custcanv",
            message,
            level: (!!retryHost) ? XdSentry.Severity.Error : XdSentry.Severity.Critical,
            data: {
                exception: errorData,
            }
        });
        XdSentry.captureMessage(
            title,
            (!!retryHost) ? XdSentry.Severity.Error : XdSentry.Severity.Critical
        );
    };

    saveProductState = async (stateId = null) => {
        if (isDevXd()) console.log("CcEditor saving product state", stateId);

        let stateIdUsed = stateId ? stateId : this.stateId;
        if( this.props.itemId && !stateIdUsed ) {
            let randomStr = Math.random().toString(36);
            stateIdUsed = `${randomStr.substring(2,7)}-${this.props.itemId}-${randomStr.substring(7)}`;
        }

        return await this.callEditorMethod("saveProduct", stateIdUsed);
    };

    saveEditingState = async () => {

        let stateIdUsed = this.editingStateId;
        if( !stateIdUsed ) {
            let randomStr = Math.random().toString(36);
            stateIdUsed = `editing-${randomStr.substring(2,7)}-uc-${randomStr.substring(7)}`;
            this.editingStateId = stateIdUsed;
        }
        return await this.callEditorMethod("saveProduct", stateIdUsed);
    };

    loadWatermarkPdf = async (save = false) => {
        try{
            if(save){
                await this.saveProductState(this.stateId);
            }
            let watermarkPdf = (await this.callCCApi(this.props.ccApis.getWatermarkPdf, {stateId: this.stateId})).pdf
            if(save && watermarkPdf){
                downloadProofs(watermarkPdf, this.props.itemId, null);
            }
            isDevXd() ? console.log("cc-editor watermarkPdf", watermarkPdf) : null;
            return watermarkPdf
        }
        catch (e) {
            console.log("Couldn't load watermark pdf " , e);
            return null;
        }
    };


    initialize = async ({userData, config}) => {
        try{
            let stateId = userData.ccStateId;
            isDevXd() ? console.log("stateId", userData.ccStateId): null;
            const stateIsValid = stateId ? await this.callCCApi(this.props.ccApis.authenticateState, stateId) : false;

            if (isDevXd()) {
                console.log("CcEditor.initialize config", config);
            }

            if(stateIsValid){
                isDevXd() ? console.log("TIME_START ", Math.floor(Date.now() / 1000)): null;
                this.stateId = stateId;
                await this.loadCanvas(config, stateId);
            }
            else{
                await this.loadCanvas(config);
            }

            // Get the item list.
            await this.reloadItems();

            isDevXd() ? console.log("TIME_FINISH ", Math.floor(Date.now() / 1000)): null;

            this.setState({
                currentSurfaceIndex: this.product.currentSurfaceIndex,
                initialized: true,
            });

            this.editor.eval(
                "let jq = document.createElement('script');" +
                "jq.src = 'https://unpkg.com/imask';" +
                "document.getElementsByTagName('head')[0].appendChild(jq)"
            );
        }
        catch (e) {
            this.setState({
                proofImages: ["assets/SPINNER_ERROR.svg"],
                currentSurfaceIndex: 0,
                initialized: false,
                error: true
            });
        }

    };

    loadCanvas = async (config, state = null) => {
        const iframe = this.editorDom;

        let productDefinition = config.productDefinition;
        if( this.props.idmlPath ) {
            //Restructuring for use of a single idml file, needs to be an object vs an array when doing single surface like base templates.
            let replaceProductDefinition = {"surfaces": {"file": this.props.idmlPath, "names": []}};
            for( let index = 0; index < productDefinition.surfaces.length; index++ ) {
                if( productDefinition.surfaces[index] ) {
                    replaceProductDefinition.surfaces.names.push( productDefinition.surfaces[index].name );
                }
            }
            productDefinition = replaceProductDefinition;
        }
        let editorConfig = config.editorConfigurations;
        const cc_authentication = await this.callCCApi(this.props.ccApis.getToken);
        const userId = cc_authentication.userGUID;
        const tokenId = cc_authentication.tokenId;
        /* TODO: temporary override to force placeholder image inserts.
        Not compatible with photo dashboard as placeholders are created with no image gallery
        As from admin level and no order is associated, we have issues with adding images from
        Headshot Gallery, Previous orders, and Photo Album.
        */
        let configuration = {...editorConfig,
            userId,
            tokenId,
            widgets: {...editorConfig.widgets, ...{
                AssetManager: {
                    tabs: [
                        {//can't remove galleries here since the state needs them for order creation. But V6 crashes with empty tabs so set all to upload.
                            "name": "Designation Gallery",
                            "assetSourceInstance": "Designation Gallery",
                            "iconClassName": "cc-icon-uploadable",
                            "controls": { "toolbarEnabled": false }
                        },
                        {
                            "name": "Headshot Gallery",
                            "assetSourceInstance": "Headshot Gallery",
                            "iconClassName": "cc-icon-uploadable",
                            "controls": { "toolbarEnabled": false }
                        },
                        {
                            "name": "Property Photos",
                            "assetSourceInstance": "Property Photos",
                            "iconClassName": "cc-icon-uploadable",
                            "controls": { "toolbarEnabled": false }
                        },
                        {
                            "name": "Agent Logo Gallery",
                            "assetSourceInstance": "Agent Logo Gallery",
                            "iconClassName": "cc-icon-uploadable",
                            "controls": { "toolbarEnabled": false }
                        },
                        {
                            "name": "Image Gallery",
                            "assetSourceInstance": "Image Gallery",
                            "iconClassName": "cc-icon-uploadable",
                            "controls": { "toolbarEnabled": false }
                        }
                    ]
                },
                BottomToolbar: {
                    fullScreenButtonEnabled: false,
                    fullWindowButtonEnabled: false,
                    rotateButtonEnabled: false,
                    surfaceSwitch: {
                        enabled: false
                    }
                },
                Toolbox: {
                    buttons: [
                        {
                          translationKey: "Toolbox.TEXT",
                          translationKeyTitle: "Toolbox.TITLE_ADD_TEXT",
                          iconClass: "cc-icon-add-text",
                          buttons: [
                            "BoundedText",
                            "RichText"
                          ]
                        },
                        {
                            iconClass: "cc-icon-add-image",
                            action: "CustomPlaceholder",
                            itemConfig: {
                                "location": {
                                    "originX": "left",
                                    "originY": "top",
                                    "x": 10,
                                    "y": 10
                                }
                            }
                        },
                        {
                          translationKey: "Toolbox.SHAPE",
                          translationKeyTitle: "Toolbox.TITLE_ADD_SHAPE",
                          iconClass: "cc-icon-add-shape",
                          buttons: [
                            "Line",
                            "rectangle",
                            "Ellipse"
                          ]
                        },
                        {
                          translationKey: "Toolbox.LINEAR_BARCODE",
                          translationKeyTitle: "Toolbox.TITLE_ADD_LINEAR_BARCODE",
                          iconClass: "cc-icon-qr-code",
                          buttons: [
                            "LinearBarcode",
                            "QrCode"
                          ]
                        }
                    ]
                }
            }},
            assetSources:{
                "Designation Gallery": {
                    "type": "PrivateSource",
                    "allowedExtensions": [ "jpg", "jpeg", "png", "svg", "pdf" ],
                    "path": `designations`,
                    "actionMode": {
                        "svg": "Shape",
                        "pdf": "Vector"
                    }
                },
                "Headshot Gallery": {
                    "type": "PrivateSource",
                    "allowedExtensions": [ "jpg", "jpeg", "png", "svg", "pdf" ],
                    "path": `headshots`,
                    "actionMode": {
                        "svg": "Shape",
                        "pdf": "Vector"
                    }
                },
                "Property Photos": {
                    "type": "PrivateSource",
                    "allowedExtensions": [ "jpg", "jpeg", "png", "svg", "pdf" ],
                    "path": `order`,
                    "actionMode": {
                        "svg": "Shape",
                        "pdf": "Vector"
                    }
                },
                "Agent Logo Gallery": {
                    "type": "PrivateSource",
                    "allowedExtensions": [ "jpg", "jpeg", "png", "svg", "pdf" ],
                    "path": "logos",
                    "actionMode": {
                        "svg": "Shape",
                        "pdf": "Vector"
                    }
                },
                "Image Gallery": {
                    "type": "PrivateSource",
                    "allowedExtensions": [ "jpg", "jpeg", "png", "svg", "pdf" ],
                    "actionMode": {
                        "svg": "Shape",
                        "pdf": "Vector"
                    }
                },
            }
        };

        await loadScript({src: this.props.ccApis.scriptUrl, id: "CcIframeApiScript"});

        const CustomersCanvas = window["CustomersCanvas"] || null;

        this.ccVersion = CustomersCanvas.VERSION;
        if (state && this.props.ccHostResponse.value?.ccVersion && !this.ccVersion.startsWith(this.props.ccHostResponse.value.ccVersion)) {
            isDevXd() ? console.log(`State version changed from ${this.props.ccHostResponse.value?.ccVersion} to ${this.ccVersion}, removing state ${state} from order`) : null;
            state = null;
        }

        // load the editor for an existing state if available
        if (state) {
            this.editor = await CustomersCanvas.IframeApi.loadEditor(iframe, state, {...configuration});
            this.product = await this.callEditorMethod("getProduct");
        } else {
            // initialize a new editor and state if no existing state
            this.editor = await CustomersCanvas.IframeApi.loadEditor(iframe, productDefinition, {...configuration});
            this.product = await this.callEditorMethod("getProduct");
        }

        //For debugging purposes
        //@ts-ignore
        window.editor = this.editor;

        resizeGripHandles(this.editor, 20);
        resizeRotationHandles(this.editor, 10);
        hideIsDoneButton(this.editor);

        // hide select image buttons when in advanced mode
        hideSelectImageButtons(this.editor);

        this.subscribeToBoundsChanges(this.editor, CustomersCanvas);
        this.subscribeToItemAdded(this.editor, CustomersCanvas);
        this.subscribeToItemRemoved(this.editor, CustomersCanvas);
        //Undo and Redo Product don't exist in 5.22
        this.subscribeToUndo(this.editor, CustomersCanvas);
        this.subscribeToRedo(this.editor, CustomersCanvas);
        this.subscribeToRevert(this.editor, CustomersCanvas);
    };

    subscribeToBoundsChanges = async (editor, CustomersCanvas) => {
        let modifiedItem = this.state.modifiedItem;
        editor.subscribe(CustomersCanvas.IframeApi.PostMessage.Events.BoundsNotification,
             async (args) => {
                let product = await this.callEditorMethod("getProduct");
                //check if a different field was selected, then unset the background color for the old one and set the color for the new one
                if(!args.selectedGroup.items[0]) {
                    let elements = document.getElementsByClassName('form');
                    for (let i = 0; i < elements.length; i++) {
                        elements[i].style.backgroundColor = '';
                    }
                    this.setState({modifiedItem: null});
                }
                else if(modifiedItem !== args.selectedGroup.items[0]) {
                    if(modifiedItem && document.getElementById(modifiedItem)) {
                        document.getElementById(modifiedItem).style.backgroundColor = '';
                    }
                    this.setState({
                        modifiedItem: args.selectedGroup.items[0],
                        modifiedSurface: product?.surfaces[product.currentSurfaceIndex].name
                    });
                    modifiedItem = args.selectedGroup.items[0];
                    if(document.getElementById(modifiedItem)) {
                        document.getElementById(modifiedItem).style.backgroundColor = 'rgba(51,65,80,0.08)';
                        if (document.getElementById(modifiedItem).getBoundingClientRect().top + 50 > window.innerHeight) {
                            document.getElementById(modifiedItem).scrollIntoView(false);
                        }

                        if (document.getElementById(modifiedItem).getBoundingClientRect().bottom < 50) {
                            document.getElementById(modifiedItem).scrollIntoView();
                        }
                    }
                }
                else if(modifiedItem && modifiedItem == args.selectedGroup.items[0]) {
                    if(!document.getElementById(modifiedItem)?.style?.backgroundColor) {
                        this.setState({modifiedItem: args.selectedGroup.items[0]});
                        document.getElementById(modifiedItem).style.backgroundColor = 'rgba(51,65,80,0.08)';
                        if (document.getElementById(modifiedItem).getBoundingClientRect().top + 50 > window.innerHeight) {
                            document.getElementById(modifiedItem).scrollIntoView(false);
                        }

                        if (document.getElementById(modifiedItem).getBoundingClientRect().bottom < 50) {
                            document.getElementById(modifiedItem).scrollIntoView();
                        }
                    }
                }
            });
    }

    subscribeToItemAdded = (editor, CustomersCanvas) => {
        editor.subscribe(CustomersCanvas.IframeApi.PostMessage.Events.ItemAdded,
            async (item) => {
                let ccVersionMajor = +this.ccVersion?.split(".")[0] || 0;
                if(ccVersionMajor >= 7 && item.type == "BoundedTextItem" && item.textPermissions?.allowTextFormatting != true ){
                    item.textPermissions.allowTextFormatting = false;
                    await this.product.setItem(item);
                }
                await this.reloadItems(true)

            }
        );
    }

    subscribeToItemRemoved = (editor, CustomersCanvas) => {
        editor.subscribe(CustomersCanvas.IframeApi.PostMessage.Events.ItemRemoved,
            async () => await this.reloadItems(true)
        );
    }

    subscribeToUndo = (editor, CustomersCanvas) => {
        editor.subscribe(CustomersCanvas.IframeApi.PostMessage.Events.UndoProduct,
            async () => await this.reloadItems(true)
        );
    }

    subscribeToRedo = (editor, CustomersCanvas) => {
        editor.subscribe(CustomersCanvas.IframeApi.PostMessage.Events.RedoProduct,
            async () => await this.reloadItems(true)
        );
    }

    subscribeToRevert = (editor, CustomersCanvas) => {
        editor.subscribe(CustomersCanvas.IframeApi.PostMessage.Events.RevertProduct,
            async () => await this.reloadItems(true)
        );
    }

    reloadItems = async(reload = false) => {
        let productModel = await this.product.getProductModel();
        let allItems = await productModel.getAllItems();
        let items = allItems.filter((item) => (item.name !== "" && item._parentContainer._name === "Main"));
        this.items = items.toReversed();
        if(reload){
            this.props.reload();
        }
    }

    render() {
        const {next, userData, ccConfig} = this.props;
        const {currentSurfaceIndex, fullScreen, initialized, error, modifiedItem} = this.state;
        return cs(
            ({}, next) => fragments(
                next(),
                userData && ccConfig && Invoke({
                    onMounted: () => this.initialize({userData, config: ccConfig}),
                }),
            ),
            () => next({
                iframeRef: (r) => this.editorDom = r,
                ccStateId: this.stateId,
                ccVersion: this.ccVersion,
                currentSide: currentSurfaceIndex,
                editor: this.editor,
                editingStateId: this.editingStateId,
                fullScreen,
                initialized,
                error,
                items: this.items,
                selectedItemName: modifiedItem,
                surfaces: this.product?.surfaces,

                changeSide: async (surfaceIndex, editing) => {
                    if (editing) {
                        await this.product.switchTo(this.product.surfaces[surfaceIndex]);
                    }
                    this.setState({currentSurfaceIndex: surfaceIndex});
                },

                // ---- START --- Refactor #4
                subscribe: (eventType, action) => {

                    this.editor.subscribe(
                        eventType,
                        action
                    );
                },
                removeEditorSelectImageButtonPositionData: () => {
                    // remove select image button position information from the iframe so that it's not available for use
                    // when using the white select image button that accompanies an existing image selection
                    removeEditorSelectImageButtonPositionData(this.editor);
                },
                clearSelection: () => {
                    this.callEditorMethod("clearSelection");
                },
                getProduct: async () => {
                    return await this.callEditorMethod("getProduct");
                },

                hideDefaultAssetManager: () => {
                    this.editor.eval("document.head.insertAdjacentHTML('afterbegin','<style>.manager-wrapper {display:none !important;}</style>')");
                },
                editorEval: (script) => this.editor.eval(script),
                callEditorMethod: (name, ...params) => this.callEditorMethod(name, ...params),
                // getEditorContext: () => this.editor._context,
                getEditor: () => this.editor,
                // ---- END --- Refactor #4

                saveProduct: async (stateId = this.stateId) => {
                    let saved = await this.saveProductState(stateId)
                    this.stateId = saved.stateId;
                    return saved;
                },

                saveEditingState: async () => {
                    return await this.saveEditingState();
                },

                saveProductModel: async () => {
                    if (!this.product) {
                        return;
                    }
                    let productModel = await this.product.getProductModel();

                    this.setState({
                        productModel: productModel
                    });
                },

                loadProductModel: async () => {
                    try{
                        await this.product.setProductModel(this.state.productModel);
                        await this.product.switchTo(this.product.surfaces[currentSurfaceIndex]);
                    }
                    catch(e){
                        console.log("Error loading product modal " , e);
                    }
                },

                loadWatermarkPdf: async (save) => {
                    const watermarkPdf = await this.loadWatermarkPdf(save);
                    this.setState({
                        watermarkPdf,
                    });
                },

                clearProofImages: () => {
                    this.setState({
                        proofImages: null,
                        watermarkPdf: null,
                    });
                },

                openFullScreenMode: () => {
                    this.setState({fullScreen: true});
                },
                closeFullScreenMode: () => {
                    this.setState({fullScreen: false});
                },

                reloadItems: async() => await this.reloadItems(),
            }),
        );
    }
}

const hideIsDoneButton = (editor) => {
    editor.eval("document.head.insertAdjacentHTML('afterbegin','<style> div button[title~=Done] {display:none;}</style>')");
};

const hideSelectImageButtons = (editor) => {
    editor.eval("document.head.insertAdjacentHTML('afterbegin','<style>.cc-icon-select-image {display:none !important;}</style>')");
}

// removes existing select image button position information - this should be called when making any selection within
// the editor so that any previous select image button clicks aren't used for determining the designer image to edit
const removeEditorSelectImageButtonPositionData = (editor) => {
    editor.eval([
        "if (window.xdData && window.xdData.LastSelectImageButtonPosition) {", // process only if the xdData variable exists
        "   delete window.xdData.LastSelectImageButtonPosition;", // delete button position information
        isDevXd() && "console.log('FROM IFRAME: Removed select image button position information');",
        "}",
    ].filter(v=>v).join(""));

}

const resizeGripHandles = (editor, size) => {
    editor.eval("spEditor.productHandler._canvas._resizeGripSize = " + size);
    editor.eval("spEditor._canvas._updateGripsAndHoverDiv()");
};

const resizeRotationHandles = (editor, size) => {
    return;
};